import type { Difficulty } from '../../shared/game/encounter-budget.js';
import { compositionKey, generateEncounter, type CompositionRules } from '../../shared/game/encounter-generator.js';
import { distance } from '../../shared/navigation/geometry.js';
import { encounterThemes, type EncounterMember, type EncounterThemeId } from './roles.js';

/** A place on a map that holds an encounter. Authored per map; its crew is generated. */
export interface EncounterSite {
  id: string; name: string; x: number; z: number;
  difficulty: Exclude<Difficulty, 'trivial'>;
  /** The faction that holds the site, which decides the roles its crew draws from. */
  theme: EncounterThemeId;
  /** Roles the crew must contain. */
  include?: EncounterMember[];
  /** A loose moving crew, or a stationary crew with selected perimeter walkers. */
  stance: 'patrol' | 'lookouts';
  /** Relative loop: patrol displacements, or a lookout's perimeter positions. */
  route: number[][];
  /** Spoils beyond the difficulty's standard purse (see `ember-coast-supplies.ts`). */
  loot?: Record<string, number>;
}

export interface EncounterPlan {
  id: string; name: string; x: number; z: number;
  difficulty: Exclude<Difficulty, 'trivial'>;
  members: EncounterMember[];
  loot?: Record<string, number>;
}

export interface PlanOptions {
  /** Names the map, so the same site id on another map composes differently. */
  mapId: string;
  party: { heroes: number; level: number };
  /** Sites closer than this (metres) never field the same crew. */
  neighbourhood?: number;
  rules?: Partial<CompositionRules>;
}

/** Turns a map's sites into plans: one generated crew per site, deterministic for a map and its party. */
export function planEncounters(sites: readonly EncounterSite[], options: PlanOptions): EncounterPlan[] {
  const plans: EncounterPlan[] = [], neighbourhood = options.neighbourhood ?? 80;
  for (const site of sites) {
    const avoid = new Set(plans.filter(plan => distance(plan, site) < neighbourhood).map(plan => compositionKey(plan.members)));
    const members = generateEncounter({ seed: `${options.mapId}/${site.id}`, difficulty: site.difficulty, party: options.party, theme: encounterThemes[site.theme],
      ...site.include ? { include: site.include } : {}, avoid, ...options.rules ? { rules: options.rules } : {} }) as EncounterMember[];
    plans.push({ id: site.id, name: site.name, x: site.x, z: site.z, difficulty: site.difficulty, members, ...site.loot ? { loot: site.loot } : {} });
  }
  return plans;
}
